// Google OAuth Callback Handler
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef for the processed flag to prevent race conditions under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');
        
        // Check if this is a linking flow
        const searchParams = new URLSearchParams(location.search);
        const isLinking = searchParams.get('link') === 'true';
        const linkToUserId = sessionStorage.getItem('linkGoogleToUser');

        if (!sessionId) {
          console.error('No session_id found in URL');
          navigate('/auth', { replace: true });
          return;
        }

        // Exchange session_id for user data
        const response = await axios.post(
          `${API}/api/google-auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data.user) {
          // If this is a linking flow, link to existing account
          if (isLinking && linkToUserId) {
            try {
              const token = localStorage.getItem('token');
              await axios.post(
                `${API}/api/auth/link-google`,
                null,
                {
                  params: {
                    google_email: response.data.user.email,
                    google_name: response.data.user.name,
                    google_picture: response.data.user.picture
                  },
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              
              // Update local storage with Google info
              const userData = JSON.parse(localStorage.getItem('user'));
              userData.google_connected = true;
              userData.google_name = response.data.user.name;
              userData.google_picture = response.data.user.picture;
              localStorage.setItem('user', JSON.stringify(userData));
              
              // Clear linking state
              sessionStorage.removeItem('linkGoogleToUser');
              
              toast.success('Google account linked successfully!');
              navigate('/settings', { replace: true });
              return;
            } catch (linkError) {
              console.error('Failed to link Google account:', linkError);
              toast.error(linkError.response?.data?.detail || 'Failed to link Google account');
              sessionStorage.removeItem('linkGoogleToUser');
              navigate('/settings', { replace: true });
              return;
            }
          }
          
          // Regular sign-in flow
          // Store user info in localStorage for quick access
          localStorage.setItem('googleUser', JSON.stringify(response.data.user));
          
          // Navigate to dashboard with user data
          const userRole = response.data.user.role;
          const redirectPath = userRole === 'seller' ? '/dashboard' : '/marketplace';
          
          navigate(redirectPath, { 
            replace: true,
            state: { user: response.data.user }
          });
        } else {
          throw new Error('No user data received');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/auth', { 
          replace: true,
          state: { error: 'Authentication failed. Please try again.' }
        });
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
        <p className="text-white text-lg">Completing sign in...</p>
        <p className="text-slate-400 text-sm mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
}
